/**
 * Roster Session page — the standalone full-screen Shift Planner, served at
 * roster.{env}.tricorder.icu/sessions/{id}. It mounts the SAME board-first
 * experience as the Home detail pane (RosterWorkspace), so the two homes cannot
 * drift: the roster board is the agent's face and the optimizer is the hero,
 * NOT a chat box.
 *
 * This page owns only the standalone chrome — leave-to-twin (ReturnToMainAgent),
 * in-app back, the title, and Share (session sharing is reachable here, not in
 * the pane). Everything roster — the plan, publish, discard, cancelling a
 * shift, the demoted Troi chat, author-vs-viewer — lives in RosterWorkspace.
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, UserPlus } from 'lucide-react'
import { useSession } from '@frontend/hooks/useSessions'
import { useAuthStore } from '@frontend/stores/auth'
import { Skeleton } from '@frontend/components/ui/skeleton'
import { Button } from '@frontend/components/ui/button'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import ShareDialog from '@frontend/components/ShareDialog'
// The SAME face the main app mounts in-frame (it discovers this module by
// globbing apps/<slug>/src/face.tsx). Mounting it here too is what makes the
// two representations one implementation.
import Face from '../face'

export default function RosterSession() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const sessionId = id || ''

  const { data: session, isLoading } = useSession(sessionId)
  const currentUserId = useAuthStore((s: ReturnType<typeof useAuthStore.getState>) => s.user?.id ?? '')
  const [shareOpen, setShareOpen] = useState(false)

  // Author gate for the Share button only (the workspace enforces read-only for
  // a viewer; the server's sessionEditor gate is the real boundary).
  const isAuthor = session?.session.created_by.performer === currentUserId

  if (!id) return null

  if (isLoading) {
    return (
      <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
        <header className="flex items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <ReturnToMainAgent className="-ml-2" />
          <Skeleton className="h-6 w-40" />
        </header>
        <div className="flex flex-1 items-center justify-center">
          <Skeleton className="h-[420px] w-full max-w-3xl rounded-[18px]" />
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
        <header className="flex items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <ReturnToMainAgent className="-ml-2" />
        </header>
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="text-[17px] text-[var(--color-surface-mute)]">
            {t('session.notFound', 'Conversation not found')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      {/* Header — House (leave-to-twin) then in-app back chevron (distinct). */}
      <header className="flex shrink-0 items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <ReturnToMainAgent className="-ml-2" />
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          aria-label={t('common.back', 'Back')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--color-surface-mute)] transition active:scale-95 hover:bg-[var(--color-neutral-100)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[19px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
            {session.session.title || t('roster.newRoster', 'New roster')}
          </h1>
        </div>
        {isAuthor && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareOpen(true)}
            aria-label={t('roster.share', 'Share')}
            className="min-h-11 shrink-0 gap-1.5 px-3 text-[13px]"
            data-testid="roster-share"
          >
            <UserPlus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t('roster.share', 'Share')}</span>
          </Button>
        )}
      </header>

      {/* The board-first workspace — shared with the Home detail pane. */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Face session={session} />
      </main>

      {isAuthor && (
        <ShareDialog
          resourceId={sessionId}
          resourceTitle={session.session.title || t('roster.shareFallbackTitle', 'Shift roster')}
          resourceType="session"
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </div>
  )
}
